import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { vendorApi } from '@/features/vendor/api/vendorApi';
import { Package, Plus, Trash2, Edit } from 'lucide-react';
import { fmt } from '@/lib/format';
import { getPackageDisplayName } from '@/lib/packageDisplay';

export default function VendorPackagesList() {
    const [packages, setPackages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const navigate = useNavigate();

    const loadData = async () => {
        setLoading(true);
        try {
            const pkgData = await vendorApi.getPackages();
            setPackages(pkgData || []);
        } catch (e) {
            setError(e?.detail || 'خطا در دریافت اطلاعات');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    const handleDeletePackage = async (id) => {
        if (!window.confirm('آیا از حذف این سبد مطمئن هستید؟')) return;
        try {
            await vendorApi.deletePackage(id);
            await loadData();
        } catch (e) {
            alert('خطا در حذف سبد');
        }
    };

    if (loading) return <div style={{ textAlign: 'center', padding: '3rem' }}>در حال دریافت اطلاعات...</div>;

    return (
        <div style={{ direction: 'rtl', width: '100%', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>

            {/* هدر صفحه */}
            <div className="admin-page-header" style={{ marginBottom: 0 }}>
                <div>
                    <h1 className="admin-page-title" style={{ fontSize: '1.25rem' }}>سبدهای فروش گروهی من</h1>
                    {/* زیرعنوان حذف شد */}
                </div>
                <button onClick={() => navigate('/vendor/packages/create')} className="admin-refresh-btn" style={{ background: '#2563eb', color: '#fff', padding: '0.5rem 1rem' }}>
                    <Plus size={16} /><span>ایجاد سبد جدید</span>
                </button>
            </div>

            {error && <div className="admin-table-error">{error}</div>}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '0.5rem' }}>
                {packages.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '4rem', background: '#fff', borderRadius: '12px', border: '1px dashed #cbd5e1', color: '#64748b', fontSize: '1.1rem' }}>
                        هنوز هیچ سبد فروش گروهی ایجاد نکرده‌اید.
                    </div>
                ) : packages.map(pkg => {
                    const hasCapacity = pkg.max_capacity !== null && pkg.max_capacity !== undefined;
                    const soldCount = Number(pkg.sold_count) || 0;
                    const maxCapacity = hasCapacity ? Number(pkg.max_capacity) : null;
                    const isSoldOut = hasCapacity && soldCount >= maxCapacity;
                    const progressPercent = hasCapacity && maxCapacity > 0
                        ? (soldCount / maxCapacity) * 100
                        : 0;

                    return (
                        <div key={pkg.id} style={{ background: '#fff', padding: '0.75rem 1rem', borderRadius: '8px', border: '1px solid #e5e7eb', display: 'flex', flexWrap: 'wrap', gap: '1rem', justifyContent: 'space-between', alignItems: 'center', boxShadow: '0 1px 2px rgba(0,0,0,0.02)' }}>

                            {/* بخش راست: نام و اطلاعات */}
                            <div style={{ flex: '1 1 300px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                                    <h3 style={{ margin: 0, fontSize: '1rem', color: '#1e293b' }}>{getPackageDisplayName(pkg.name)}</h3>
                                    {pkg.is_private ? (
                                        <span style={{ background: '#fef3c7', color: '#92400e', padding: '2px 6px', borderRadius: '4px', fontSize: '0.7rem', fontWeight: 'bold' }}>اختصاصی</span>
                                    ) : (
                                        <span style={{ background: '#dcfce7', color: '#15803d', padding: '2px 6px', borderRadius: '4px', fontSize: '0.7rem', fontWeight: 'bold' }}>عمومی</span>
                                    )}
                                    {pkg.is_referral_enabled && (
                                        <span style={{ background: '#e0e7ff', color: '#3730a3', padding: '2px 6px', borderRadius: '4px', fontSize: '0.7rem', fontWeight: 'bold' }}>دارای پورسانت</span>
                                    )}
                                    {isSoldOut && (
                                        <span style={{ background: '#ef4444', color: '#fff', padding: '2px 6px', borderRadius: '4px', fontSize: '0.7rem', fontWeight: 'bold' }}>تمام شد</span>
                                    )}
                                </div>
                                <div style={{ color: '#64748b', fontSize: '0.8rem' }}>
                                    تعداد اقلام: {fmt(pkg.items?.length ?? 0)} | درصد تخفیف: <span style={{ fontWeight: 'bold', color: '#2563eb' }}>{fmt(pkg.discount_percent)}٪</span>
                                </div>
                            </div>

                            {/* بخش وسط: وضعیت ظرفیت */}
                            <div style={{ flex: '1 1 200px', background: '#f8fafc', padding: '0.4rem 0.75rem', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem', fontSize: '0.75rem', fontWeight: 'bold' }}>
                                    <span style={{ color: '#64748b' }}>وضعیت فروش:</span>
                                    <span style={{ color: isSoldOut ? '#ef4444' : '#2563eb' }}>
                                        {hasCapacity
                                            ? `${fmt(soldCount)} فروش از ظرفیت ${fmt(maxCapacity)}`
                                            : `${fmt(soldCount)} فروش · ظرفیت نامحدود`}
                                    </span>
                                </div>
                                {hasCapacity ? (
                                    <div style={{ height: '4px', background: '#e2e8f0', borderRadius: '2px', overflow: 'hidden' }}>
                                        <div style={{ height: '100%', width: `${Math.min(progressPercent, 100)}%`, background: isSoldOut ? '#ef4444' : '#3b82f6' }} />
                                    </div>
                                ) : (
                                    <div style={{ height: '4px', background: '#e2e8f0', borderRadius: '2px' }} />
                                )}
                            </div>

                            {/* بخش چپ: دکمه‌ها */}
                            <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                                <button onClick={() => navigate(`/vendor/packages/edit/${pkg.id}`)} style={{ background: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe', padding: '0.4rem 0.75rem', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                                    <Edit size={14} /> ویرایش
                                </button>
                                <button onClick={() => handleDeletePackage(pkg.id)} style={{ background: '#fef2f2', color: '#ef4444', border: '1px solid #fecaca', padding: '0.4rem 0.75rem', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                                    <Trash2 size={14} /> حذف
                                </button>
                            </div>

                        </div>
                    )
                })}
            </div>
        </div>
    );
}